import { Component, Inject, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ReservationSummaryStoreService } from '../../reservation-summary/services/reservation-summary-store.service';
import { ReservationSummaryApiService } from '../../reservation-summary/services/reservation-summary-api.service';
import { Attachment, Conversation, ConversationMessages } from '../../reservation.model';
import { catchError, Observable, Subscription } from 'rxjs';
import { DataBagService } from '@/common/data-bag.service';
import { ReservationMessageDetailsDialogComponent } from './reservation-message-details-dialog/reservation-message-details-dialog.component';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { FormControl, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthBagService } from '@/auth/auth-bag.service';
import { environment } from 'src/environments/environment';
import { ReservationMessageGalleryComponent } from './reservation-message-gallery/reservation-message-gallery.component';
import { DownloadFileService } from '@/common/download-file.service';
import { ActivatedRoute } from '@angular/router';

@Component({
	selector: 'app-reservation-message-dialog',
	templateUrl: './reservation-message-dialog.component.html',
	styleUrls: ['./reservation-message-dialog.component.scss'],
})
export class ReservationMessageDialogComponent implements OnInit, OnDestroy {
	constructor(
		@Optional()
		@Inject(MAT_DIALOG_DATA)
		public dialogData: {
			conversation: Conversation;
		},
		private readonly dialog: MatDialog,
		@Optional()
		public dialogRef: MatDialogRef<ReservationMessageDialogComponent>,
		protected store: ReservationSummaryStoreService,
		protected api: ReservationSummaryApiService,
		protected bag: DataBagService,
		protected infobox: InfoboxService,
		private readonly sanitizer: DomSanitizer,
		private readonly http: HttpClient,
		private readonly auth: AuthBagService,
		public downloadService: DownloadFileService,
		private readonly route: ActivatedRoute,
	) {}

	protected answer = new FormControl('', [Validators.required]);
	data$: Observable<ConversationMessages[]>;
	showAnswer = false;
	private dataSubscription: Subscription;
	attachmentsList: Attachment[][] = [];
	@ViewChild(ReservationMessageGalleryComponent, { static: false })
	protected reservationMessageGallery: ReservationMessageGalleryComponent;
	private loadedImagesCount = 0;
	protected isImagesLoaded: boolean[] = [];
	conversationId: string;
	protected iframeDefaultHeight = 200;
	protected iframeConditionMap: Map<number, boolean> = new Map<number, boolean>();

	ngOnInit() {
		if (this.dialogData) {
			this.conversationId = this.dialogData.conversation.id;
		} else this.conversationId = this.route.snapshot.params['id'];
		const { getAttachments, getMessages } = this.api.actions();
		getMessages(this.conversationId);
		this.data$ = this.store.getters().messages();
		this.dataSubscription = this.data$.subscribe((messages) => {
			messages.forEach((message, i) => {
				this.convertMessage(message);
				this.isImagesLoaded.push(true);
				if (message.attachmentCount) {
					getAttachments(this.conversationId, message.id)
						.then((value) => {
							this.attachmentsList[i] = value;
						})
						.catch((error) => {
							throw new Error(JSON.stringify(error.error));
						});
				} else this.attachmentsList[i] = [];
				this.iframeConditionMap.set(i, true);
			});
		});
	}

	private convertMessage(message: ConversationMessages) {
		const isUnread: boolean = message.labels.includes('unread');
		message.sender = this.removeSpecificSymbolsFromString(message.sender);
		message.addressee = this.removeSpecificSymbolsFromString(message.addressee);
		if (isUnread) {
			message.show = !!isUnread;
			message.labels = message.labels.filter((label) => label !== 'unread');
			this.updateLabels(message);
		}
		return message;
	}

	protected removeGmailAnswers(value: string): SafeHtml {
		const regex = /<blockquote\b[^>]*>[\s\S]*?<\/blockquote>|<div class="gmail_quote">[\s\S]*?<\/div>/gi;
		return this.sanitizer.bypassSecurityTrustHtml(value.replace(regex, ''));
	}

	private removeSpecificSymbolsFromString(value: string): string {
		return value.replace(/"/g, '');
	}

	private updateLabels(message: ConversationMessages) {
		this.bag.put(
			`/conversation/threads/${this.conversationId}/messages/${message.id}/`,
			{ labels: message.labels },
			(result, error) => {
				if (error) throw new Error(JSON.stringify(error.error));
			},
		);
	}

	protected openDetailsDialog(position: HTMLElement, data: ConversationMessages) {
		const offset = 20;
		this.dialog.open(ReservationMessageDetailsDialogComponent, {
			data: data,
			position: {
				left: `${position.getBoundingClientRect().x}px`,
				top: `${position.getBoundingClientRect().y + offset}px`,
			},
			backdropClass: 'backdropDetails',
			enterAnimationDuration: '0ms',
			exitAnimationDuration: '0ms',
		});
	}

	protected closeAnswer() {
		this.answer.reset();
		this.showAnswer = false;
	}

	private httpHeadersGet() {
		if (typeof this.auth.tokenGet() === 'undefined') {
			return new HttpHeaders({
				'Content-Type': 'application/json',
			});
		}
		return new HttpHeaders({
			'Content-Type': 'application/json',
			Authorization: 'Bearer ' + this.auth.tokenGet(),
		});
	}

	private getAttachment(url: string): Promise<string> {
		return new Promise((resolve, reject) => {
			this.http
				.get(environment.apiUrl + url, {
					responseType: 'blob',
					headers: this.httpHeadersGet(),
				})
				.pipe(
					catchError((error) => {
						reject(error);
						throw new Error(JSON.stringify(error.error));
					}),
				)
				.subscribe((result) => {
					const reader = new FileReader();
					reader.readAsDataURL(result);

					reader.onloadend = () => {
						resolve(reader.result as string);
					};
				});
		});
	}

	getAttachmentsList(messageIndex: number, message: ConversationMessages) {
		if (!message.attachmentCount) return;
		const attachments = this.attachmentsList[messageIndex];
		this.isImagesLoaded[messageIndex] = false;
		attachments.forEach((attachment) => {
			const url = `/conversation/threads/${this.conversationId}/messages/${message?.id}/attachments/${attachment?.id}/`;
			this.getAttachment(url).then((value) => {
				attachment.src = value;
				if (!attachment.mimeType.includes('image')) {
					this.isImagesLoaded[messageIndex] = true;
				}
			});
		});
	}

	protected hideAttachmentList(messageIndex: number) {
		this.attachmentsList[messageIndex].forEach((value) => {
			value.src = '';
		});
	}

	protected downloadAllAttachments(messageIndex: number) {
		this.attachmentsList[messageIndex].forEach((_, i) => {
			this.downloadService.downloadAttachment(this.attachmentsList[messageIndex][i]);
		});
	}

	protected isAttachmentsShow(i: number): boolean {
		return this.attachmentsList[i]?.some((v) => v.src);
	}

	protected setFileIconClass(fileType: string): string {
		switch (true) {
			case fileType.includes('html'):
				return 'fa-file-code';
			case fileType.includes('zip'):
				return 'fa-file-archive';
			case fileType.includes('pdf'):
				return 'fa-file-pdf';
			default:
				return 'fa-file';
		}
	}

	protected imageLoad(i: number) {
		this.loadedImagesCount++;
		if (this.loadedImagesCount === this.attachmentsList[i].filter((v) => v.mimeType.includes('image')).length) {
			this.isImagesLoaded[i] = true;
			this.loadedImagesCount = 0;
		}
	}

	protected adjustIframeHeight(iframe: HTMLIFrameElement, index: number) {
		const { height, contentWindow } = iframe;
		if (contentWindow.document.body.scrollHeight < this.iframeDefaultHeight) return;
		if (+height === contentWindow.document.body.scrollHeight) {
			this.iframeConditionMap.set(index, true);
			iframe.height = this.iframeDefaultHeight.toString();
		} else {
			this.iframeConditionMap.set(index, false);
			iframe.height = contentWindow.document.body.scrollHeight.toString();
		}
	}
	ngOnDestroy() {
		this.dataSubscription?.unsubscribe();
	}
}
